import dayjs, { Dayjs } from 'dayjs'
import { capitalize } from 'lodash'
import { match, P } from 'ts-pattern'
import { ChargeForecast, ChargeSchedule } from '@/types'
import { Vehicle } from '@/types/vehicle'
import {
  ManufacturerConnectionReason,
  VehicleConnectivity,
} from '@/types/vehicle/vehicleConnectivity'
import { getIsCharging } from '@/authenticated/components/ChargeForecastDashboardView/utils'
import { timeAgo } from '@/utils/date'
import { isTesla } from '@/utils/vehicles/car'
import {
  teslaMissingPermissions,
  teslaMissingVirtualKey,
} from '@/utils/vehicles/car/permissions'
import {
  ChargeCardLayout,
  RatePlanKind,
  StatusAlert,
  StatusTone,
} from './ChargeForecastV2.types'
import { getRatePlanKind } from './UpcomingRatesCard.utils'
import {
  getPeriodMetric,
  getSelectedMetricKey,
} from './ChargeSessionDetails.utils'

// Same "connection broken" threshold as the Plug Sessions page
const STALE_PING_DAYS = 14
// Same buffer the charge forecast utils give the scheduled Ready By time
const DEPARTURE_BUFFER_MINUTES = 5
const BATTERY_TARGET_PADDING_PERCENT = 2

// The charge_now_status values that come with a Charge Now button
export const CHARGE_NOW_ACTION_STATUSES = [
  'startable',
  'boostable',
  'stoppable',
]

export type ChargeStatusKind =
  | 'ConnectionBroken'
  | 'ForecastUnavailable'
  | 'NoHome'
  | 'NotPluggedIn'
  | 'Override'
  | 'AwayFromHome'
  | 'SettingUp'
  | 'NotControlling'
  | 'ChargeComplete'
  | 'ForecastEmpty'
  | 'NoGoals'
  | 'Optimizing'

/** Which banner sent the customer to the Report Issue form. */
export type ReportTopic = 'forecastFailed' | 'forecastEmpty' | 'batteryUnknown'

export type ChargeStatus = {
  kind: ChargeStatusKind
  tone: StatusTone
  layout: ChargeCardLayout
  title: string
  subtitle: string | null
  // When the car last reported, shown on every state
  lastUpdated: string | null
  alert: StatusAlert | null
  reportTopic: ReportTopic | null
  isCharging: boolean
  showRateTiers: boolean
  showUpcomingRates: boolean
  // The "why is my car paused / charging" info icon; there's nothing to
  // explain while the car is unplugged
  showWhyInfo: boolean
  // Whether Charge Now fits this state at all. The backend decides when it's
  // available, but it doesn't check charge control, so the card rules out
  // the states where starting a charge means nothing to the driver
  allowsChargeNow: boolean
}

/**
 * The charge settings the backend is actually optimizing to, which for a
 * program with a shadow rate (PSEG-LI, PNM) differ from the saved schedule.
 */
export type ActiveChargeFlags = {
  moneySaving: boolean
  savePlanet: boolean
  solarSurplus: boolean
  scheduleDeparture: boolean
  allowChargingControl: boolean
}

export type ForecastDevice = 'vehicle' | 'charger'

export type ChargeStatusInputs = {
  // A charger knows less than a car: no battery level, no limit, no location
  device?: ForecastDevice
  // Chargers have no connectivity endpoint; a lapsed login is their only
  // "can't reach it" signal
  authInactive?: boolean
  // The energy the backend plans a charger session around (kWh)
  plannedEnergyKwh?: number
  chargeForecast: ChargeForecast | undefined
  forecastFailed: boolean
  chargeSchedule: ChargeSchedule | undefined
  vehicle: Vehicle | undefined
  connectivity: VehicleConnectivity | undefined
  isInActiveDREvent: boolean
  // The DR event banner renders above this card and takes the alert slot
  hasDREventAlert: boolean
  // The driver opted out of an event that is running now
  optedOutOfActiveEvent: boolean
  // The device gallery already shows a connection or permission banner for
  // this vehicle (lost authentication, missing Tesla key or permissions)
  hasDeviceAlertBanner: boolean
  // The account's rate plan is still being set up by the utility
  hasPendingRatePlan: boolean
  // The forecast came back as "no home": the account has no home address
  forecastHasNoHome: boolean
  batteryPercent: number | null
  chargeLimitPercent: number
  // From active_charge_settings; absent until that request lands
  activeSettings?: ActiveChargeFlags
  now: Dayjs
}

export function getIsConnectionBroken(
  connectivity: VehicleConnectivity | undefined,
  vehicle: Vehicle | undefined,
  now: Dayjs,
) {
  const lastPingIsStale = vehicle?.last_ping
    ? dayjs(vehicle.last_ping).isBefore(now.subtract(STALE_PING_DAYS, 'day'))
    : false

  return Boolean(
    connectivity?.manufacturer_connection?.is_auth_expired ||
      connectivity?.manufacturer_connection?.reason ||
      vehicle?.is_not_found ||
      lastPingIsStale,
  )
}

/**
 * Whether the device gallery already shows a connection or permission banner
 * for this vehicle. Mirrors the always-shown cases of vehicleAlertState in
 * DeviceSelectionGallery's useDeviceAlertStates, without its hooks.
 */
export function getHasDeviceAlertBanner(vehicle: Vehicle | undefined) {
  if (!vehicle) {
    return false
  }

  return Boolean(
    vehicle.authentication_inactive ||
      (isTesla(vehicle) &&
        (vehicle.requires_tesla_fleet_migration ||
          teslaMissingVirtualKey(vehicle.car) ||
          teslaMissingPermissions(vehicle.car, 'vehicle_device_data'))),
  )
}

export function getConnectionBrokenMessage(
  connectivity: VehicleConnectivity | undefined,
  vehicle: Vehicle | undefined,
) {
  const make = vehicle?.car?.car_model?.make
  return connectivity?.manufacturer_connection?.reason ===
    ManufacturerConnectionReason.PublicKeyNotAttached
    ? `We can’t control your ${
        make ? capitalize(make) : 'vehicle'
      } due to a missing capability.`
    : 'We can’t reach your vehicle right now.'
}

/**
 * Optiwatt is shaping this charge when the driver set a goal, a Ready By time
 * or a trip, or the car is in a utility event.
 */
export function getIsOptimizing(
  chargeSchedule: ChargeSchedule | undefined,
  chargeForecast: ChargeForecast | undefined,
  isInActiveDREvent: boolean,
  activeSettings?: ActiveChargeFlags,
) {
  const hasGoal = activeSettings
    ? activeSettings.moneySaving ||
      activeSettings.savePlanet ||
      activeSettings.solarSurplus ||
      activeSettings.scheduleDeparture
    : Boolean(
        chargeSchedule?.money_saving_plan_enabled ||
          chargeSchedule?.save_the_planet_plan_enabled ||
          chargeSchedule?.solar_surplus_plan_enabled ||
          chargeSchedule?.schedule_departure_enabled,
      )

  return Boolean(
    hasGoal ||
      chargeSchedule?.is_trip ||
      chargeForecast?.is_trip ||
      isInActiveDREvent,
  )
}

/** Charge control as the backend applies it, program overrides included. */
export function getAllowsChargingControl(inputs: ChargeStatusInputs) {
  return inputs.activeSettings
    ? inputs.activeSettings.allowChargingControl
    : Boolean(inputs.chargeSchedule?.allow_charging_control)
}

/**
 * The charge can't reach the limit by the Ready By time: no forecast period
 * that ends before departure gets the battery to the limit.
 */
export function getDoomedCharge(args: {
  chargeForecast: ChargeForecast
  chargeSchedule: ChargeSchedule | undefined
  chargeLimitPercent: number
  batteryPercent: number
  now: Dayjs
}) {
  const {
    chargeForecast,
    chargeSchedule,
    chargeLimitPercent,
    batteryPercent,
    now,
  } = args
  const periods = chargeForecast.charge_forecast_periods ?? []

  if (
    !chargeSchedule?.schedule_departure_enabled ||
    !chargeForecast.departure_time_utc ||
    !periods.length
  ) {
    return null
  }

  const departure = dayjs(chargeForecast.departure_time_utc)
  const alreadyAtLimit =
    batteryPercent >= chargeLimitPercent - BATTERY_TARGET_PADDING_PERCENT

  if (!departure.isAfter(now) || alreadyAtLimit) {
    return null
  }

  const deadline = departure.add(DEPARTURE_BUFFER_MINUTES, 'minute')
  const metricKey = getSelectedMetricKey(chargeForecast.selected_plan)
  const reachesLimitInTime = periods.some(
    (period) =>
      !dayjs(period.end_datetime).isAfter(deadline) &&
      (getPeriodMetric(period, 'end', metricKey)?.battery_percent ?? 0) >=
        chargeLimitPercent - BATTERY_TARGET_PADDING_PERCENT,
  )

  return reachesLimitInTime ? null : { chargeLimitPercent, departure }
}

export function getChargeStatusKind(
  inputs: ChargeStatusInputs,
): ChargeStatusKind {
  return (
    match(inputs)
      .when(
        (i) =>
          Boolean(i.authInactive) ||
          getIsConnectionBroken(i.connectivity, i.vehicle, i.now),
        () => 'ConnectionBroken' as const,
      )
      .when(
        (i) => i.forecastHasNoHome,
        () => 'NoHome' as const,
      )
      .when(
        (i) => i.forecastFailed || !i.chargeForecast,
        () => 'ForecastUnavailable' as const,
      )
      // Settings problems show whether or not the car is plugged in: the
      // driver has to fix them either way
      .when(
        (i) => !i.chargeSchedule,
        () => 'SettingUp' as const,
      )
      .when(
        (i) => !getAllowsChargingControl(i),
        () => 'NotControlling' as const,
      )
      .when(
        (i) => !i.chargeForecast?.plugged_in,
        () => 'NotPluggedIn' as const,
      )
      .when(
        (i) => i.chargeForecast?.charge_now_status === 'stoppable',
        () => 'Override' as const,
      )
      // A utility event applies wherever the car is, so during one the card
      // reads as optimizing rather than as away from home
      .when(
        (i) => i.chargeForecast?.is_home === false && !i.isInActiveDREvent,
        () => 'AwayFromHome' as const,
      )
      // A charger can't see the battery, so it never declares the charge done
      .when(
        (i) =>
          i.device !== 'charger' &&
          getIsChargeComplete({
            batteryPercent: i.batteryPercent,
            chargeLimitPercent: i.chargeLimitPercent,
            isCharging: getIsCharging(i.chargeForecast),
          }),
        () => 'ChargeComplete' as const,
      )
      .when(
        (i) =>
          !getIsOptimizing(
            i.chargeSchedule,
            i.chargeForecast,
            i.isInActiveDREvent,
            i.activeSettings,
          ),
        () => 'NoGoals' as const,
      )
      .when(
        (i) => !i.chargeForecast?.charge_forecast_periods?.length,
        () => 'ForecastEmpty' as const,
      )
      .otherwise(() => 'Optimizing' as const)
  )
}

function getPlugStateTitle(chargeForecast: ChargeForecast | undefined) {
  return match(chargeForecast)
    .when(
      (forecast) => getIsCharging(forecast),
      () => 'Charging',
    )
    .when(
      (forecast) => Boolean(forecast?.plugged_in),
      () => 'Plugged in, not charging',
    )
    .otherwise(() => 'Not plugged in')
}

function getLastUpdated(vehicle: Vehicle | undefined) {
  return vehicle?.last_ping
    ? `Last Updated: ${timeAgo(vehicle.last_ping)}`
    : null
}

/**
 * Plugged in with the battery already at the limit: there's nothing left to
 * charge, so the card has no session to show.
 */
export function getIsChargeComplete(args: {
  batteryPercent: number | null
  chargeLimitPercent: number
  isCharging: boolean
}) {
  const { batteryPercent, chargeLimitPercent, isCharging } = args

  return (
    !isCharging &&
    batteryPercent !== null &&
    batteryPercent >= chargeLimitPercent - BATTERY_TARGET_PADDING_PERCENT
  )
}

function getOptimizingAlert(args: {
  doomedCopy: string | null
  planKind: RatePlanKind
  hasDREventAlert: boolean
  hasPendingRatePlan: boolean
}): StatusAlert | null {
  return match(args)
    .with(
      { doomedCopy: P.string },
      ({ doomedCopy }): StatusAlert => ({
        // Orange: the driver needs to act (plug in earlier or move Ready By)
        variant: 'warning',
        placement: 'forecast',
        message: `There isn’t enough time to reach ${doomedCopy}.`,
        link: { text: 'Learn more', action: 'learnMoreChargeShortfall' },
      }),
    )
    .with({ hasDREventAlert: true }, () => null)
    .with(
      { planKind: 'none', hasPendingRatePlan: true },
      (): StatusAlert => ({
        variant: 'info',
        placement: 'rates',
        message:
          'Your rate plan is still being set up. Prices appear here once it’s ready.',
      }),
    )
    .with(
      { planKind: 'none' },
      (): StatusAlert => ({
        variant: 'info',
        placement: 'rates',
        message:
          'To see what each plug session costs, add your electricity rate plan.',
        link: { text: 'Add rate plan', action: 'addRatePlan' },
      }),
    )
    .otherwise(() => null)
}

/**
 * Whether the card shows the Charge Now button: the backend offers it and the
 * state allows it. A broken connection shows no button either.
 */
export function getShowChargeButton(
  status: ChargeStatus,
  chargeNowStatus: string | undefined,
) {
  return (
    status.allowsChargeNow &&
    status.layout !== 'minimal' &&
    CHARGE_NOW_ACTION_STATUSES.includes(chargeNowStatus ?? '')
  )
}

export function getChargeStatus(inputs: ChargeStatusInputs): ChargeStatus {
  const { chargeForecast, chargeSchedule, vehicle, connectivity, now } = inputs
  const kind = getChargeStatusKind(inputs)
  const device: ForecastDevice = inputs.device ?? 'vehicle'
  const deviceNoun = device === 'charger' ? 'charger' : 'car'
  const planKind = getRatePlanKind(chargeForecast?.plan)
  const lastUpdated = getLastUpdated(vehicle)
  const plugStateTitle = getPlugStateTitle(chargeForecast)
  const common = {
    kind,
    lastUpdated,
    reportTopic: null,
    isCharging: getIsCharging(chargeForecast),
    showRateTiers: planKind === 'tou',
    showUpcomingRates: true,
    // Without a forecast the header falls back to "Not plugged in" too
    showWhyInfo: Boolean(chargeForecast?.plugged_in),
    allowsChargeNow: true,
  }

  const status = match(kind)
    .with(
      'ConnectionBroken',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'minimal',
        title: plugStateTitle,
        // The header only explains when no banner does: the gallery's own
        // connection banner sits above the whole section, so the card then
        // says why it's empty, and otherwise the in-card banner says it
        subtitle: inputs.hasDeviceAlertBanner
          ? `Optiwatt can’t reach your ${
              device === 'charger' ? 'charger' : 'vehicle'
            }`
          : null,
        // Don't repeat the gallery's own connection banner
        alert: inputs.hasDeviceAlertBanner
          ? null
          : {
              variant: 'warning',
              message:
                device === 'charger'
                  ? 'We can’t reach your charger right now.'
                  : getConnectionBrokenMessage(connectivity, vehicle),
              link: { text: 'Fix this now', action: 'fixConnection' },
            },
      }),
    )
    .with(
      'ForecastUnavailable',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'collapsed',
        title: 'Status unavailable',
        // The banner in the forecast section says what's wrong
        subtitle: null,
        // Same wording and Report Issue form as the app's crash fallback (Oops).
        // Orange: the forecast failing is always our fault, never the driver's
        alert: {
          variant: 'warning',
          placement: 'forecast',
          message:
            'Something went wrong with the Charge Forecast. We’re on it.',
          link: { text: 'Report Issue', action: 'reportIssue' },
        },
        reportTopic: 'forecastFailed',
        showUpcomingRates: false,
      }),
    )
    .with(
      'NotPluggedIn',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'collapsed',
        title: 'Not plugged in',
        subtitle: null,
        alert: null,
      }),
    )
    .with(
      'Override',
      (): ChargeStatus => ({
        ...common,
        tone: 'optimizing-charging',
        layout: 'full',
        // Charge Non-stop is on, but the car may not have started yet. The
        // shared isCharging helper counts an override as charging, so read
        // the car's own flag here
        title: chargeForecast?.is_charging ? 'Charging' : 'Starting charge',
        subtitle: chargeForecast?.is_charging
          ? 'You started this charge'
          : 'Waiting for your car to start charging',
        alert: null,
      }),
    )
    .with(
      'AwayFromHome',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'collapsed',
        title: plugStateTitle,
        // Not a designed screen: use the same quiet card as Not plugged in
        subtitle: null,
        alert: null,
        // Optiwatt only controls charging at home
        allowsChargeNow: false,
      }),
    )
    .with(
      'NoHome',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'collapsed',
        title: plugStateTitle,
        subtitle: 'Add your home to see your charge plan',
        alert: {
          variant: 'info',
          message: 'Add home info to see this charge session.',
          link: { text: 'Add Home', action: 'addHome' },
        },
        showUpcomingRates: false,
      }),
    )
    .with(
      'SettingUp',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'collapsed',
        title: plugStateTitle,
        // The banner says it
        subtitle: null,
        alert: {
          variant: 'info',
          message: `We’re still setting up charging for this ${deviceNoun}. This can take a few minutes.`,
        },
      }),
    )
    .with(
      'ChargeComplete',
      (): ChargeStatus => ({
        ...common,
        tone: 'optimizing-charging',
        layout: 'collapsed',
        title: 'Charge complete',
        subtitle:
          device === 'charger'
            ? `Delivered the planned ${inputs.plannedEnergyKwh} kWh`
            : `Charged to your ${inputs.chargeLimitPercent}% limit`,
        alert: null,
        // Nothing is paused or charging, so there's nothing to explain
        showWhyInfo: false,
        // Already at the limit: there's nothing left to charge
        allowsChargeNow: false,
      }),
    )
    .with(
      'ForecastEmpty',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'collapsed',
        title: plugStateTitle,
        subtitle: null,
        // Orange: a plugged-in car with no plan means we're missing battery
        // telemetry or prices, which is a bug on our side
        alert: {
          variant: 'warning',
          placement: 'forecast',
          message:
            'We don’t have a charge plan for this session yet. Check back soon.',
          link: { text: 'Report Issue', action: 'reportIssue' },
        },
        reportTopic: 'forecastEmpty',
      }),
    )
    .with(
      'NotControlling',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'collapsed',
        title: plugStateTitle,
        // The banner says it
        subtitle: null,
        // Informational, not a fault: the driver chose this
        alert: {
          variant: 'info',
          message: 'Optiwatt is not controlling your charging.',
          link: {
            text: 'Turn on charge control',
            action: 'turnOnChargeControl',
          },
        },
        // Optiwatt isn't holding the charge back, so there's nothing to override
        allowsChargeNow: false,
      }),
    )
    .with(
      'NoGoals',
      (): ChargeStatus => ({
        ...common,
        tone: 'not-optimizing',
        layout: 'collapsed',
        title: plugStateTitle,
        // The header explains only when no banner does
        subtitle: inputs.optedOutOfActiveEvent
          ? 'You opted out of this event'
          : inputs.hasDREventAlert
          ? 'No optimization running'
          : null,
        // The event banner above already says what's happening and offers
        // Opt In; a second "nothing is optimizing you" contradicts it
        alert: inputs.hasDREventAlert
          ? null
          : {
              variant: 'info',
              message: 'Nothing is optimizing your charging yet.',
              link: { text: 'Choose a goal', action: 'chooseGoal' },
            },
      }),
    )
    .with('Optimizing', (): ChargeStatus => {
      const doomed = chargeForecast
        ? getDoomedCharge({
            chargeForecast,
            chargeSchedule,
            chargeLimitPercent: inputs.chargeLimitPercent,
            batteryPercent: inputs.batteryPercent ?? 0,
            now,
          })
        : null
      const doomedCopy = doomed
        ? `${doomed.chargeLimitPercent}% by ${doomed.departure.format(
            'h:mm A',
          )}`
        : null

      return {
        ...common,
        tone: common.isCharging ? 'optimizing-charging' : 'optimizing-paused',
        layout: 'full',
        title: common.isCharging ? 'Charging' : 'Paused',
        // The banner in the forecast section says a doomed charge won't make it
        subtitle: null,
        alert: getOptimizingAlert({
          doomedCopy,
          planKind,
          hasDREventAlert: inputs.hasDREventAlert,
          hasPendingRatePlan: inputs.hasPendingRatePlan,
        }),
      }
    })
    .exhaustive()

  // A car that isn't reporting its battery level can't be planned around,
  // unless the card already says the car can't be reached at all
  const connectionIsTheProblem = [
    'ConnectionBroken',
    'ForecastUnavailable',
    'NoHome',
  ].includes(kind)
  return status.alert ||
    inputs.batteryPercent !== null ||
    connectionIsTheProblem ||
    // A charger never has a battery reading to be missing
    device === 'charger'
    ? status
    : {
        ...status,
        // Orange: with the connection fine, no battery reading is our problem
        alert: {
          variant: 'warning' as const,
          placement: 'battery' as const,
          message: 'We’re not getting a battery level from your car right now.',
          link: {
            text: 'Report Issue' as const,
            action: 'reportIssue' as const,
          },
        },
        reportTopic: 'batteryUnknown' as const,
      }
}
